import AddIcon from '@mui/icons-material/Add';
import Button from '@mui/material/Button';
import './Schedule.css';
import { useState, useEffect } from 'react';
import { Box, Typography, Menu, MenuItem, CircularProgress, Fade } from '@mui/material';
import Layout from '../../../components/Layout';
import TeacherNavbar from '../../../components/TeacherNavbar/TeacherNavbar';
// import { useTranslation } from "../../../i18n/LanguageContext";
import CalendarView from '../../../components/Schedule/CalenderView/CalenderView';
import SessionAvailability from '../../../components/Schedule/SessionAvailability/SessionAvailability';
import PreviewModal from '../../../components/Schedule/PreviewModel/PreviewModel';
// import CreateSessionModal from "../../../components/Schedule/CreateSessionModel/CreateSessionMenu";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import OneOnOneIcon from '../../../assets/Dashboard/oneoneonesession.svg';
import GroupIcon from '../../../assets/Dashboard/group.svg';
import PrimeIcon from '../../../assets/Dashboard/prime.svg';
import SmartPauseIcon from '../../../assets/SessionIcon/smart-pause-icon.svg';
import CreateOneOnOneSessionModel from '../../../components/Schedule/CreateSessionModel/CreateOneOnOneSessonModel/CreateOneOnOneSessonModel';
import CreateGroupSessionModel from '../../../components/Schedule/CreateSessionModel/CreateGroupSessonModel/CreateGroupSessonModel';
import CreatePrimeSessionModel from '../../../components/Schedule/CreateSessionModel/CreatePrimeSessonModel/CreatePrimeSessonModel';
import PauseAvailabilityModel from '../../../components/Schedule/PauseAvailabilityModel/PauseAvailabilityModel';
import LoadingScreen from '../../../components/LoadingIcon/LoadingScreen';
import { axiosInstance } from '../../../utils/tokenUtils';
import { triggerCalendarRefresh } from '../../../utils/calendarRefresh';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import TransactionHistorySkeleton, {
  RevenueSectionSkeleton,
} from '../../../components/SkeletonEffect/SkeletonEffect';

// Extend dayjs with UTC and timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

// Utility function to convert UTC time to local time
export const convertUtcToLocal = (dateString, timeString) => {
  // Check if dateString is a UTC ISO timestamp (ends with Z)
  if (dateString && typeof dateString === 'string' && dateString.endsWith('Z')) {
    // Parse the UTC timestamp and convert to local
    const localDate = dayjs(dateString).local();
    // Return in YYYY-MM-DD format
    return localDate.format('YYYY-MM-DD');
  }

  // If only timeString is provided (for recurring availability)
  if (timeString && !dateString) {
    // Create a UTC time today with the given hours and minutes
    const [hours, minutes] = timeString.split(':').map(Number);
    const utcTime = dayjs.utc().hour(hours).minute(minutes).second(0);
    // Convert to local time
    const localTime = utcTime.local();
    // Return in HH:MM format
    return localTime.format('HH:mm');
  }

  // If both date and time are provided (for specific date overrides)
  if (dateString && timeString) {
    // Parse the date and time in UTC
    const [hours, minutes] = timeString.split(':').map(Number);
    const utcDateTime = dayjs.utc(`${dateString}T${timeString}`);
    // Convert to local time
    const localDateTime = utcDateTime.local();

    // For date+time, return an object with separated components
    return {
      date: localDateTime.format('YYYY-MM-DD'),
      time: localDateTime.format('HH:mm'),
    };
  }

  // If only date is provided (could be a full ISO timestamp without Z)
  if (dateString && !timeString) {
    // Check if dateString contains time part (T separator)
    if (dateString.includes('T')) {
      // Treat as UTC ISO format without Z marker
      const utcDate = dayjs.utc(dateString);
      // Convert to local time
      const localDate = utcDate.local();
      // Return in full ISO format
      return localDate.format('YYYY-MM-DDTHH:mm:ss');
    } else {
      // Just a date without time
      const utcDate = dayjs.utc(dateString);
      const localDate = utcDate.local();
      return localDate.format('YYYY-MM-DD');
    }
  }

  // Return original if invalid input
  return timeString || dateString;
};

// Function to convert a full timestamp from UTC to local time zone
export const convertTimestampFromUtcToLocal = timestamp => {
  if (!timestamp) return timestamp;

  // Parse the timestamp as UTC regardless of whether it has a Z suffix
  const utcTime = dayjs.utc(timestamp);

  // Convert to local time zone
  const localTime = utcTime.local();

  // Return in the same format as input but in local time
  return localTime.format('YYYY-MM-DDTHH:mm:ss');
};

// Process API response data to convert UTC times to local
export const processApiResponseToLocalTime = data => {
  if (!data || !Array.isArray(data)) return data;

  return data.map(session => {
    // Create a new session object to avoid mutating the original
    const localSession = { ...session };

    // Process recurring availability slots
    if (localSession.availability && Array.isArray(localSession.availability)) {
      localSession.availability = localSession.availability.map(slot => {
        return {
          ...slot,
          startTime: convertUtcToLocal(null, slot.startTime),
          endTime: convertUtcToLocal(null, slot.endTime),
        };
      });
    }

    // Process date-specific overrides
    if (localSession.overrides && Array.isArray(localSession.overrides)) {
      localSession.overrides = localSession.overrides.map(override => {
        const startLocal = convertUtcToLocal(override.date, override.startTime);
        const endLocal = convertUtcToLocal(override.date, override.endTime);

        // Check if the event crosses midnight
        const startHour = parseInt(override.startTime.split(':')[0], 10);
        const endHour = parseInt(override.endTime.split(':')[0], 10);
        const startMinute = parseInt(override.startTime.split(':')[1], 10);
        const endMinute = parseInt(override.endTime.split(':')[1], 10);

        const crossesMidnight =
          endHour < startHour || (endHour === startHour && endMinute < startMinute);

        // For events that cross midnight, we need to adjust the end date
        if (crossesMidnight) {
          // Create a date object for the end time on the next day
          const nextDate = dayjs.utc(override.date).add(1, 'day').format('YYYY-MM-DD');
          const endLocalNextDay = convertUtcToLocal(nextDate, override.endTime);

          return {
            date: startLocal.date,
            startTime: startLocal.time,
            endTime: endLocal.time,
            crossesMidnight: true,
            endDate: endLocalNextDay.date, // Store the next day's date for the end time
          };
        }

        return {
          date: startLocal.date,
          startTime: startLocal.time,
          endTime: endLocal.time,
          crossesMidnight: false,
        };
      });
    }

    return localSession;
  });
};

const SessionTypeColor = {
  one_on_one: {
    color: '#6B60E7',
    bgcolor: '#E8F0FF',
    value: 'One-On-One',
    icon: OneOnOneIcon,
  },
  group: {
    color: '#7BCF8C',
    bgcolor: '#FAFFFB',
    value: 'Group',
    icon: GroupIcon,
  },
  prime: {
    color: '#AD8B00',
    bgcolor: '#F0EFFF',
    value: 'Prime',
    icon: PrimeIcon,
  },
  smart_pause: {
    color: '#1F435D',
    bgcolor: '#F0F0F0',
    value: 'Smart Pause',
    icon: SmartPauseIcon,
  },
};

const Schedule = () => {
  // const { translate } = useTranslation();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showPreview, setShowPreview] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [createMenuAnchor, setCreateMenuAnchor] = useState(null);
  const [selectedSessionType, setSelectedSessionType] = useState(null);
  const [showOneOnOneModal, setShowOneOnOneModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showPrimeModal, setShowPrimeModal] = useState(false);
  const [showPauseAvailabilityModal, setShowPauseAvailabilityModal] = useState(false);
  const [bookedSessionsData, setBookedSessionsData] = useState([]);
  // New state for API data
  const [availabilityData, setAvailabilityData] = useState([]);
  const [pauseAvailabilityData, setPauseAvailabilityData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fadeIn, setFadeIn] = useState(false);
  const [error, setError] = useState(null);
  // New state for teacher profile data
  const [teacherProfile, setTeacherProfile] = useState(null);

  // Function to refresh all calendar data
  const refreshAllCalendarData = async () => {
    try {
      setLoading(true);
      setFadeIn(false);

      // Use Promise.all to wait for all API calls to complete
      const [teacherProfileResponse, availabilityResponse, pauseResponse, bookedSessionsResponse] =
        await Promise.all([
          axiosInstance.get('/teachers'),
          axiosInstance.get('/teachers/availability?offset=0&limit=50'),
          axiosInstance.get('/teachers/pause-availability?offset=0&limit=50'),
          axiosInstance.get('/teachers/sessions?status=upcoming&offset=0&limit=50'),
        ]);

      // Process teacher profile data
      setTeacherProfile(teacherProfileResponse.data);

      // Process availability data - extract results from paginated response
      setAvailabilityData(processApiResponseToLocalTime(availabilityResponse.data.results));

      // Process pause availability data - extract results from paginated response
      setPauseAvailabilityData(
        pauseResponse.data.results.map(pause => {
          const processedPause = { ...pause };
          if (processedPause.start) {
            processedPause.start = convertTimestampFromUtcToLocal(processedPause.start);
          }
          if (processedPause.end) {
            processedPause.end = convertTimestampFromUtcToLocal(processedPause.end);
          }
          return processedPause;
        })
      );

      // Process booked sessions data - extract results from paginated response
      setBookedSessionsData(
        bookedSessionsResponse.data.results.map(session => {
          const processedSession = { ...session };
          if (processedSession.start_time) {
            processedSession.start_time = dayjs
              .utc(processedSession.start_time)
              .local()
              .format('YYYY-MM-DDTHH:mm:ss');
          }
          return processedSession;
        })
      );

      // Only set loading to false after all data is processed and state is updated
      setLoading(false);

      // Delay fade in for a smoother transition
      setTimeout(() => {
        setFadeIn(true);
      }, 100);
    } catch (err) {
      console.error('Error refreshing calendar data:', err);
      setError('Failed to refresh calendar data');
      setLoading(false); // allow skeleton view to render instead of raw error text
    }
  };

  // Fetch availability data
  useEffect(() => {
    refreshAllCalendarData();
  }, []);

  const handlePreviousWeek = () => {
    const prevWeek = new Date(currentDate);
    prevWeek.setDate(prevWeek.getDate() - 7);
    setCurrentDate(prevWeek);
  };

  const handleNextWeek = () => {
    const nextWeek = new Date(currentDate);
    nextWeek.setDate(nextWeek.getDate() + 7);
    setCurrentDate(nextWeek);
  };

  const handleSessionClick = session => {
    console.log('Session clicked in Schedule.jsx:', session);
    setSelectedSession(session);
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  const handleCreateClick = event => {
    // First, open the menu immediately to ensure responsiveness
    setCreateMenuAnchor(event.currentTarget);

    // Then fetch the latest teacher profile data in the background
    axiosInstance
      .get('/teachers')
      .then(response => {
        setTeacherProfile(response.data);
      })
      .catch(err => {
        console.error('Error fetching teacher profile:', err);
        // The menu is already open, so no additional action needed for errors
      });
  };

  const handleCreateMenuClose = () => {
    setCreateMenuAnchor(null);
  };

  const handleSessionTypeSelect = type => {
    if (type === 'one-on-one') {
      setShowOneOnOneModal(true);
    } else if (type === 'group') {
      setShowGroupModal(true);
    } else if (type === 'prime') {
      setShowPrimeModal(true);
    } else if (type === 'smart-pause') {
      setShowPauseAvailabilityModal(true);
    } else {
      setSelectedSessionType(type);
    }
    handleCreateMenuClose();
  };

  const handleCloseOneOnOneModal = () => {
    setShowOneOnOneModal(false);
  };

  const handleCloseGroupModal = () => {
    setShowGroupModal(false);
  };

  const handleClosePrimeModal = () => {
    setShowPrimeModal(false);
  };

  const handleOneOnOneContinue = (days, description) => {
    // Handle data from the one-on-one modal
    setShowOneOnOneModal(false);
    setSelectedSessionType('one-on-one');
    // Refresh calendar after creation
    refreshAllCalendarData();
  };

  const handleGroupConfirm = groupData => {
    // Handle data from the group session modal
    console.log('Group session data:', groupData);
    setShowGroupModal(false);
    // Refresh calendar after creation
    refreshAllCalendarData();
  };

  const handlePrimeConfirm = primeData => {
    // Handle data from the prime session modal
    console.log('Prime session data:', primeData);
    setShowPrimeModal(false);
    // Refresh calendar after creation
    refreshAllCalendarData();
  };

  const handleClosePauseAvailabilityModal = () => {
    setShowPauseAvailabilityModal(false);
  };

  const handlePauseAvailabilityConfirm = pauseData => {
    // Handle data from the pause availability modal
    console.log('Pause availability data:', pauseData);
    setShowPauseAvailabilityModal(false);
    // Refresh calendar after creation
    refreshAllCalendarData();
  };

  // If loading, show a loading indicator
  if (loading) {
    return (
      <Layout title="Klass Online | Schedule">
        <Box className="relative h-screen w-full overflow-hidden">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
            <TeacherNavbar />
            <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px] flex justify-center items-center">
              <LoadingScreen />
            </Box>
          </Box>
        </Box>
      </Layout>
    );
  }

  // If there's an error, show an error message
  if (error) {
    return (
      <Layout title="Klass Online | Schedule">
        <Box className="relative h-screen w-full overflow-hidden">
          <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
            <TeacherNavbar />
            <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
              <Box className="flex flex-col p-[1rem] gap-3">
                {/* Skeleton header placeholder */}
                <Box className="flex justify-between items-center mb-4">
                  <Box sx={{ width: 200, height: 28, bgcolor: '#eaeaea', borderRadius: 1 }} />
                  <Box sx={{ width: 120, height: 40, bgcolor: '#eaeaea', borderRadius: 20 }} />
                </Box>
                <Box bgcolor={'white'} padding={'10px'}>
                  <Box className="schedule-content" sx={{ display: 'flex', width: '100%' }}>
                    <Box className="schedule-availability" sx={{ pr: 2 }}>
                      <TransactionHistorySkeleton count={5} />
                    </Box>
                    <Box className="calendar-view">
                      <RevenueSectionSkeleton />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Layout>
    );
  }
  // console.log(availabilityData);

  return (
    <Layout title="Klass Online | Schedule">
      <Box className="relative h-screen w-full overflow-hidden">
        <Box className="sticky top-0 left-0 right-0 z-[1000] bg-white pr-[10px]">
          <TeacherNavbar />
          <Box className="h-[calc(100vh-64px)] overflow-auto relative bg-[#F9F9F9] rounded-lg mr-[10px]">
            <Fade in={fadeIn} timeout={800}>
              <Box className="flex flex-col p-[1rem] gap-3">
                <Box className="flex justify-between items-center mb-4">
                  <Typography variant="h5" className="schedule-overview-title">
                    Schedule Overview
                  </Typography>
                  <Button
                    color="primary"
                    startIcon={<AddIcon style={{ color: '#fff' }} />}
                    endIcon={<KeyboardArrowDownIcon style={{ color: '#fff' }} />}
                    className="create-button"
                    onClick={handleCreateClick}
                  >
                    <Typography className="create-button-text">Create</Typography>
                  </Button>
                  <Menu
                    anchorEl={createMenuAnchor}
                    open={Boolean(createMenuAnchor)}
                    onClose={handleCreateMenuClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    className="schedule-type-menu"
                    PaperProps={{
                      style: {
                        width: '600px',
                        borderRadius: '8px',
                        border: '1px solid var(--Stroke, #D5DEE3)',
                        background: '#FFF',
                        boxShadow: ' 0px 6px 10.3px 0px rgba(10, 13, 18, 0.05)',
                        display: 'inline-flex',
                        padding: '12px',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        gap: '12px',
                      },
                    }}
                    MenuListProps={{
                      style: {
                        padding: '0',
                      },
                    }}
                  >
                    <Typography className="schedule-types-title">Session Types</Typography>

                    {/* Only show One-on-One session if allowed in teacher profile */}
                    {teacherProfile?.allow_one_on_one_session && (
                      <MenuItem
                        onClick={() => handleSessionTypeSelect('one-on-one')}
                        className="schedule-menu-item"
                      >
                        <Box className="schedule-menu-content">
                          <Box
                            className="schedule-menu-header"
                            sx={{ color: SessionTypeColor.one_on_one.color }}
                          >
                            <Box className="schedule-menu-icon one-on-one">
                              <img src={OneOnOneIcon} alt="One-on-one session" />
                            </Box>
                            <Typography className="schedule-type-title">
                              One-On-One Session
                            </Typography>
                          </Box>
                          <Box className="schedule-menu-details">
                            <Typography className="schedule-type-subtitle">
                              1 Mentor -&gt; 1 Student
                            </Typography>
                            <Typography className="schedule-type-description">
                              A personalized session where a student books an individual class with
                              the teacher.
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    )}

                    {/* Only show Group session if allowed in teacher profile */}
                    {teacherProfile?.allow_group_session && (
                      <MenuItem
                        onClick={() => handleSessionTypeSelect('group')}
                        className="schedule-menu-item"
                      >
                        <Box className="schedule-menu-content">
                          <Box
                            className="schedule-menu-header"
                            sx={{ color: SessionTypeColor.group.color }}
                          >
                            <Box
                              className="schedule-menu-icon group"
                              sx={{ backgroundColor: SessionTypeColor.group.bgcolor }}
                            >
                              <img src={GroupIcon} alt="Group session" />
                            </Box>
                            <Typography className="schedule-type-title">Group Session</Typography>
                          </Box>
                          <Box className="schedule-menu-details">
                            <Typography className="schedule-type-subtitle">
                              1 Mentor -&gt; Multiple Student
                            </Typography>
                            <Typography className="schedule-type-description">
                              A collaborative learning session where multiple students join a
                              scheduled class.
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    )}

                    {/* Only show Prime session if allowed in teacher profile */}
                    {teacherProfile?.allow_prime_session && (
                      <MenuItem
                        onClick={() => handleSessionTypeSelect('prime')}
                        className="schedule-menu-item"
                      >
                        <Box className="schedule-menu-content">
                          <Box
                            className="schedule-menu-header"
                            sx={{ color: SessionTypeColor.prime.color }}
                          >
                            <Box
                              className="schedule-menu-icon prime"
                              sx={{ backgroundColor: SessionTypeColor.prime.bgcolor }}
                            >
                              <img src={PrimeIcon} alt="Prime session" />
                            </Box>
                            <Typography className="schedule-type-title">Prime Session</Typography>
                          </Box>
                          <Box className="schedule-menu-details">
                            <Typography className="schedule-type-subtitle">
                              1 Mentor -&gt; Multiple Sessions
                            </Typography>
                            <Typography className="schedule-type-description">
                              A collaborative learning session where multiple students join a
                              Multiple scheduled class.
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    )}

                    {/* Show divider only if at least one session type is shown above */}
                    {(teacherProfile?.allow_one_on_one_session ||
                      teacherProfile?.allow_group_session ||
                      teacherProfile?.allow_prime_session) && <Box className="menu-divider"></Box>}

                    {/* Always show Smart Pause as there's no restriction on it */}
                    <MenuItem
                      onClick={() => handleSessionTypeSelect('smart-pause')}
                      className="schedule-menu-item"
                    >
                      <Box className="schedule-menu-content">
                        <Box
                          className="schedule-menu-header"
                          sx={{ color: SessionTypeColor.smart_pause.color }}
                        >
                          <Box
                            className="schedule-menu-icon smart-pause"
                            sx={{ backgroundColor: SessionTypeColor.smart_pause.bgcolor }}
                          >
                            <img src={SmartPauseIcon} alt="Smart pause" />
                          </Box>
                          <Typography className="schedule-type-title">Smart Pause</Typography>
                        </Box>
                        <Box className="schedule-menu-details">
                          <Typography className="schedule-type-subtitle">
                            Mentor -&gt; Pause Availability
                          </Typography>
                          <Typography className="schedule-type-description">
                            Set your unavailability period, and your sessions will remain hidden
                            until you're ready.
                          </Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  </Menu>
                </Box>
                <Box bgcolor={'white'} padding={'10px'}>
                  <Box className="schedule-content" sx={{ display: 'flex', width: '100%' }}>
                    <Box className="schedule-availability" sx={{ pr: 2 }}>
                      <SessionAvailability
                        onEyeClick={handleSessionClick}
                        sessionsData={availabilityData}
                        pauseSessionsData={pauseAvailabilityData}
                      />
                    </Box>
                    <Box className="calendar-view">
                      <CalendarView
                        currentDate={currentDate}
                        onPrevWeek={handlePreviousWeek}
                        onNextWeek={handleNextWeek}
                        onSessionClick={handleSessionClick}
                        sessionsData={availabilityData}
                        pauseSessionsData={pauseAvailabilityData}
                        bookedSessionsData={bookedSessionsData}
                        onRefreshRequest={refreshAllCalendarData}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Fade>
          </Box>
        </Box>
      </Box>

      {showPreview && (
        <PreviewModal
          session={selectedSession}
          onClose={handleClosePreview}
          sessionsData={availabilityData}
          pauseSessionsData={pauseAvailabilityData}
        />
      )}

      {showOneOnOneModal && (
        <CreateOneOnOneSessionModel
          open={showOneOnOneModal}
          onClose={handleCloseOneOnOneModal}
          onContinue={handleOneOnOneContinue}
        />
      )}

      {showGroupModal && (
        <CreateGroupSessionModel
          open={showGroupModal}
          onClose={handleCloseGroupModal}
          onConfirm={handleGroupConfirm}
        />
      )}

      {showPrimeModal && (
        <CreatePrimeSessionModel
          open={showPrimeModal}
          onClose={handleClosePrimeModal}
          onConfirm={handlePrimeConfirm}
        />
      )}

      {showPauseAvailabilityModal && (
        <PauseAvailabilityModel
          open={showPauseAvailabilityModal}
          onClose={handleClosePauseAvailabilityModal}
          onConfirm={handlePauseAvailabilityConfirm}
        />
      )}
    </Layout>
  );
};

export default Schedule;
